// "Kontakt bestaetigen" im Kopf der Kontaktseite (ST-PTY-001, CM-PTY-008).
//
// KLARE HAUPTAKTION (5.2 Nr. 2, NFR-UX-004): die Seite behaelt "Notiz
// festhalten" als einzige primaere Aktion; der Knopf hier ist sekundaer und
// erscheint nur an einem Entwurf. Ein aktiver Kontakt zeigt nur sein
// Kennzeichen im Kopf.
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005): fehlt etwas, steht genau EIN Mangel mit
// dem Weg dorthin da. Der Weg zum Namen oeffnet den Abschnitt auf dieser
// Seite; eine doppelte E-Mail-Adresse nennt den anderen Kontakt, sofern die
// Pruefung ihn findet. Behebt die Nutzerin den Mangel auf dieser Seite,
// verschwindet der Hinweis, statt als Fehlschlag stehen zu bleiben.
//
// DEAKTIVIEREN UND WIEDER AUFNEHMEN (CM-PTY-008): "Kontakt deaktivieren" ist
// ein Menueeintrag in "Weitere Aktionen" (kopf.tsx), die seltene, folgenreiche
// Aktion gehoert nicht in den ersten Blick. Es fragt nach dem Grund, ohne einen
// vorzubelegen, und sagt vorher die Wirkung; Wieder aufnehmen macht einen
// Entwurf daraus, der danach wie jeder Entwurf bestaetigt wird.
//
// DIE LEISTE IST NUR SICHTBAR, WENN SIE ETWAS ZEIGT (Entwurf, inaktiv, Meldung,
// offene Grundauswahl). Sonst bleibt die Statusregion im DOM -- sie sagt
// Ergebnisse an -- und ist nur optisch verborgen, nicht display: none.
//
// BARRIEREFREIHEIT (NFR-ACC-001): eine Statusregion; der Fokus geht nach dem
// Befehl dorthin (useSectionCommands), auch wenn der Knopf danach fehlt. Beim
// Oeffnen der Grundauswahl steht er auf dem ersten Grund, nach Abbrechen auf
// "Weitere Aktionen" (APG Menu Button: zurueck zum Ausloeser).

import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Field,
  Link,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  tokens,
} from '@fluentui/react-components';

import { fetchParty, partyHref, type PartyDetail } from '../../parties.ts';
import {
  ALREADY_CONFIRMED,
  ALREADY_DRAFT,
  ALREADY_INACTIVE,
  confirmHint,
  CONFIRM_LABEL,
  confirmation,
  confirmParty,
  DEACTIVATE_EFFECT,
  DEACTIVATE_QUESTION,
  deactivateParty,
  deactivationConfirmation,
  endReasonsFor,
  reactivateParty,
  reactivationConfirmation,
  REACTIVATE_HINT,
  REACTIVATE_LABEL,
  EMAIL_DUPLICATE_MESSAGE,
  findEmailDuplicate,
  messageFor,
  nextStepsFor,
  notWritableNotice,
  PARTY_CONFIRM_MESSAGES,
  partyLockVersion,
  type PartyConfirmed,
} from '../../party-lifecycle.ts';
import { AppLink } from '../../shell/router.tsx';
import { useSectionCommands, meldungsIntent } from '../../shell/use-section-commands.ts';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  bereich: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  // Die sichtbare Leiste: neutrale Flaeche, Markenrand links wie der Kopf.
  leiste: {
    borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '0.5rem 1rem',
    borderRadius: tokens.borderRadiusMedium,
  },
  // Optisch verborgen, aber im DOM und fuer Screenreader da. Absolut: sie
  // zaehlt nicht als Flex-Kind, der Abstand von 16 px entfaellt.
  verborgen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  hinweis: {
    color: tokens.colorNeutralForeground2,
  },
  // Der Entwurf als eine Zeile (v4 notice): Warnflaeche, ein Satz und der Knopf
  // daneben; die volle Regel liest der Knopf als Beschreibung vor.
  entwurf: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    borderLeftColor: 'transparent',
    borderLeftWidth: 0,
    padding: '0.625rem 0.875rem',
  },
  entwurfZeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem 1rem',
    alignSelf: 'stretch',
  },
  entwurfSatz: {
    flex: '1 1 14rem',
    minWidth: 0,
    color: tokens.colorNeutralForeground1,
  },
  nurVorgelesen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  aktionen: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  auswahl: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxWidth: '32rem',
  },
  wege: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem 1rem',
    overflowWrap: 'anywhere',
  },
});

const HINWEIS_ID = 'kontakt-bestaetigen-hinweis';

/**
 * Der sichtbare Satz am Entwurf, je Art: eine Organisation hat keinen Nachnamen,
 * der Server verlangt dort ihren Namen. Die volle Regel (confirmHint) beschreibt
 * den Knopf.
 */
function entwurfSatz(istOrganisation: boolean): string {
  const name = istOrganisation ? 'der Name der Organisation' : 'ein Nachname';
  return `Noch ein Entwurf. Bestätige ihn, sobald ${name} und ein Kontaktweg oder eine Anschrift da sind; danach gilt er als aktiv, zurück geht es nicht.`;
}

export function StatusAktion({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  party,
  onParty,
  onZumAbschnitt,
  grundOffen,
  onGrundOffen,
  ausloeserRef,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly party: PartyDetail;
  readonly onParty: (party: PartyDetail) => void;
  /** Oeffnet einen Abschnitt dieser Seite und setzt den Fokus hinein. */
  readonly onZumAbschnitt: (abschnitt: 'name') => void;
  /** Die Grundauswahl wird ueber den Menueeintrag im Kopf geoeffnet. */
  readonly grundOffen: boolean;
  readonly onGrundOffen: (offen: boolean) => void;
  /** "Weitere Aktionen": dorthin geht der Fokus nach Abbrechen. */
  readonly ausloeserRef: RefObject<HTMLButtonElement | null>;
}): ReactElement | null {
  const own = useStyles();
  const [dublette, setDublette] = useState<{ readonly displayName: string; readonly href: string } | undefined>(undefined);
  const [hinweise, setHinweise] = useState<Pick<PartyConfirmed, 'phoneMatch' | 'emailMatch'>>({});
  const [grund, setGrund] = useState('');
  const auswahlRef = useRef<HTMLDivElement | null>(null);
  // Wird der Kontakt anders als ueber diese Auswahl undeaktivierbar (Konflikt,
  // schon inaktiv), schliesst sie; sonst oeffnete sie sich nach dem Wieder
  // aufnehmen von selbst mit dem alten Grund.
  const status = party.lifecycleStatus;
  useEffect(() => {
    if (status !== 'draft' && status !== 'active') {
      onGrundOffen(false);
      setGrund('');
    }
  }, [status, onGrundOffen]);
  useEffect(() => {
    if (!grundOffen) {
      return undefined;
    }
    // Fluents Menue gibt den Fokus beim Schliessen an den Ausloeser zurueck;
    // erst danach auf den ersten Grund, sonst gewinnt der Ausloeser.
    const zeiger = window.setTimeout(() => auswahlRef.current?.querySelector<HTMLInputElement>('input[type="radio"]')?.focus(), 0);
    return () => window.clearTimeout(zeiger);
  }, [grundOffen]);

  const befehle = useSectionCommands({
    accessToken,
    onReauthenticate,
    // Nach Konflikt oder 404: den jetzt gueltigen Stand zeigen. Scheitert der Abruf, meldet der Hook das.
    refresh: async () => {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return undefined;
      }
      const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId: party.partyId });
      if (result.outcome === 'loaded') {
        onParty(result.party);
        return undefined;
      }
      return result.outcome === 'rejected' ? result.message : PARTY_CONFIRM_MESSAGES.notFound;
    },
  });
  const { message, messageOffen, confirmation: bestaetigung, busy, statusRef, clearMessages } = befehle;

  // Ein Mangel, den die Nutzerin auf dieser Seite behoben hat, ist kein
  // Hinweis mehr. Nur die Angaben, auf die die Regel schaut; ein stilles
  // Nachladen mit demselben Stand nimmt ihn nicht weg.
  const nachname = party.person?.familyName;
  const organisationsname = party.organization?.legalName;
  const emailZahl = party.emailAddresses.length;
  const telefonZahl = party.phoneNumbers.length;
  useEffect(() => {
    setDublette(undefined);
    clearMessages();
  }, [nachname, organisationsname, emailZahl, telefonZahl, clearMessages]);

  const bestaetigen = (): void => {
    if (busy) {
      return;
    }
    setDublette(undefined);
    setHinweise({});
    void befehle.command({
      run: async (token) => {
        const outcome = await confirmParty({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId: party.partyId,
          lockVersion: partyLockVersion(party),
        });
        if (outcome.outcome === 'conflict' && outcome.message === EMAIL_DUPLICATE_MESSAGE) {
          setDublette(await findEmailDuplicate({ baseUrl: apiBaseUrl, accessToken: token, party }).catch(() => undefined));
        }
        return outcome;
      },
      confirm: confirmation,
      // Die Antwort traegt Status und Stand; der Rest der Party ist unveraendert.
      reload: false,
      onApplied: (applied) => {
        setHinweise({
          ...(applied.phoneMatch === undefined ? {} : { phoneMatch: applied.phoneMatch }),
          ...(applied.emailMatch === undefined ? {} : { emailMatch: applied.emailMatch }),
        });
        uebernehmen(applied);
      },
    });
  };

  /** Der neue Status und Stand aus der Antwort; der Rest der Party ist unveraendert. */
  const uebernehmen = (applied: PartyConfirmed): void => {
    onParty({
      ...party,
      lifecycleStatus: applied.lifecycleStatus,
      lockVersion: applied.lockVersion,
      ...(party.person === undefined ? {} : { person: { ...party.person, lockVersion: applied.lockVersion } }),
    });
  };

  const deaktivieren = (): void => {
    if (busy || grund === '') {
      return;
    }
    setDublette(undefined);
    setHinweise({});
    void befehle.command({
      run: (token) =>
        deactivateParty({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId: party.partyId,
          lockVersion: partyLockVersion(party),
          endReasonCode: grund,
        }),
      confirm: deactivationConfirmation,
      reload: false,
      onApplied: (applied) => {
        onGrundOffen(false);
        setGrund('');
        uebernehmen(applied);
      },
    });
  };

  const aufnehmen = (): void => {
    if (busy) {
      return;
    }
    setDublette(undefined);
    setHinweise({});
    void befehle.command({
      run: (token) =>
        reactivateParty({ baseUrl: apiBaseUrl, accessToken: token, partyId: party.partyId, lockVersion: partyLockVersion(party) }),
      confirm: reactivationConfirmation,
      reload: false,
      onApplied: uebernehmen,
    });
  };

  const entwurf = party.lifecycleStatus === 'draft';
  const deaktivierbar = entwurf || party.lifecycleStatus === 'active';
  const inaktiv = party.lifecycleStatus === 'inactive';
  const nurLesen = notWritableNotice(party.lifecycleStatus);
  if (!deaktivierbar && !inaktiv && nurLesen === undefined && bestaetigung === undefined && message === undefined) {
    return null;
  }
  const ohneWirkung = bestaetigung === ALREADY_CONFIRMED || bestaetigung === ALREADY_INACTIVE || bestaetigung === ALREADY_DRAFT;
  const problem = message === undefined ? undefined : messageFor(message, party);
  const wege = dublette === undefined ? nextStepsFor(message, party) : [];

  const mitInhalt = entwurf || inaktiv || nurLesen !== undefined || bestaetigung !== undefined || message !== undefined || (deaktivierbar && grundOffen);

  return (
    <div className={mergeClasses(own.bereich, mitInhalt ? own.leiste : own.verborgen, entwurf && own.entwurf)}>
      <div ref={statusRef} tabIndex={-1} role="status" aria-live="polite" aria-label="Status: Aktivierung">
        {bestaetigung !== undefined ? (
          <MessageBar intent={ohneWirkung ? 'info' : 'success'}>
            <MessageBarBody>
              <MessageBarTitle>{ohneWirkung ? 'Nichts geändert' : 'Gespeichert'}</MessageBarTitle>
              <div>{bestaetigung}</div>
              {hinweise.emailMatch === undefined ? undefined : (
                <div>
                  Dieselbe E-Mail-Adresse steht auch bei einem nicht aktiven Kontakt:{' '}
                  <AppLink to={partyHref(hinweise.emailMatch.partyId)}>{hinweise.emailMatch.displayName}</AppLink>. Prüfe bei
                  Bedarf, ob es derselbe Kontakt ist.
                </div>
              )}
              {hinweise.phoneMatch === undefined ? undefined : (
                <div>
                  Dieselbe Telefonnummer steht auch bei{' '}
                  <AppLink to={partyHref(hinweise.phoneMatch.partyId)}>{hinweise.phoneMatch.displayName}</AppLink>. Das ist oft
                  eine Zentrale; prüfe bei Bedarf, ob es derselbe Kontakt ist.
                </div>
              )}
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {problem !== undefined ? (
          <MessageBar intent={meldungsIntent(problem, messageOffen)}>
            <MessageBarBody>
              <MessageBarTitle>{problem.headline}</MessageBarTitle>
              <div>{problem.explanation}</div>
              <div>{problem.nextStep}</div>
              {dublette === undefined ? undefined : (
                <div className={own.wege}>
                  <AppLink to={dublette.href}>{`${dublette.displayName} öffnen`}</AppLink>
                </div>
              )}
              {wege.length === 0 ? undefined : (
                <div className={own.wege}>
                  {wege.map((weg) =>
                    'section' in weg ? (
                      <Link key={weg.label} as="button" onClick={() => onZumAbschnitt(weg.section)}>
                        {weg.label}
                      </Link>
                    ) : (
                      <AppLink key={weg.label} to={weg.href}>
                        {weg.label}
                      </AppLink>
                    ),
                  )}
                </div>
              )}
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>
      {entwurf ? (
        <div className={own.entwurfZeile}>
          <Body1 className={own.entwurfSatz}>{entwurfSatz(party.organization !== undefined)}</Body1>
          <span id={HINWEIS_ID} className={own.nurVorgelesen}>
            {confirmHint(party.organization !== undefined)}
          </span>
          <Button appearance="secondary" onClick={bestaetigen} disabled={busy} aria-describedby={HINWEIS_ID}>
            {CONFIRM_LABEL}
          </Button>
        </div>
      ) : undefined}
      {inaktiv ? <Caption1 className={own.hinweis}>{REACTIVATE_HINT}</Caption1> : undefined}
      {/* Eine Auskunft ueber den Zustand, kein Ergebnis einer Handlung: ausserhalb der Statusregion, ohne Ansage beim Laden. */}
      {nurLesen === undefined ? undefined : (
        <Body1 className={own.hinweis}>
          <strong>{nurLesen.headline}</strong> {nurLesen.explanation}
        </Body1>
      )}
      <div className={own.aktionen}>
        {inaktiv ? (
          <Button appearance="secondary" onClick={aufnehmen} disabled={busy}>
            {REACTIVATE_LABEL}
          </Button>
        ) : undefined}
      </div>
      {deaktivierbar && grundOffen ? (
        <div className={own.auswahl} ref={auswahlRef}>
          <Field label={DEACTIVATE_QUESTION} required>
            <RadioGroup value={grund} onChange={(_event, data) => setGrund(data.value)}>
              {endReasonsFor(party).map((eintrag) => (
                <Radio key={eintrag.code} value={eintrag.code} label={eintrag.label} />
              ))}
            </RadioGroup>
          </Field>
          <Body1>{DEACTIVATE_EFFECT}</Body1>
          <div className={own.aktionen}>
            {/* Eine destruktive Aktion ist keine Hauptaktion: die einzige primaere der Seite
                bleibt "Notiz festhalten". */}
            <Button appearance="secondary" onClick={deaktivieren} disabled={busy || grund === ''}>
              Deaktivieren
            </Button>
            <NebenKnopf
              disabled={busy}
              onClick={() => {
                setGrund('');
                onGrundOffen(false);
                window.setTimeout(() => ausloeserRef.current?.focus(), 0);
              }}
            >
              Abbrechen
            </NebenKnopf>
          </div>
        </div>
      ) : undefined}
    </div>
  );
}
